import { GetLevels } from '../api/queries/get-levels';
import type { Domain } from '../components/filters/domain-toggle';
import { BATTERY_LEVELS, type BatteryLevel } from '../entities/battery-level';
import type { Filters } from '../entities/filters';

/**
 * Helper to determine the lowest available level for a given domain
 */
export function getLowestAvailableLevel(
  domain: Domain,
  levelsData: GetLevels.Output | undefined,
): BatteryLevel | undefined {
  if (!levelsData) {
    return undefined;
  }

  if (domain === 'battery') {
    const batteryLevels = levelsData.battery;
    if (!batteryLevels) {
      return undefined;
    }

    const availableLevels = Object.keys(batteryLevels) as BatteryLevel[];
    if (availableLevels.length === 0) {
      return undefined;
    }

    // Find the lowest level by checking in reverse hierarchy order
    return BATTERY_LEVELS.slice()
      .reverse()
      .find((level) => availableLevels.includes(level));
  } else {
    const pcsLevels = levelsData.pcs;
    if (!pcsLevels || Object.keys(pcsLevels).length === 0) {
      return undefined;
    }

    // For PCS, return 'pcs' as the lowest level if available
    return 'pcs' as BatteryLevel;
  }
}

/**
 * Determines if the current selection is at the lowest available level.
 * Used to control noise filter activation - only applies to battery domain.
 * PCS data never qualifies for noise filtering as it doesn't go through
 * the same preprocessing pipeline as battery cell-level data.
 */
export function isAtLowestLevel(
  filters: Filters,
  domain: Domain,
  levelsData: GetLevels.Output | undefined,
): boolean {
  // PCS data never qualifies for noise filtering
  if (domain === 'pcs') {
    return false;
  }

  if (!levelsData) {
    return false;
  }

  const lowestLevel = getLowestAvailableLevel(domain, levelsData);
  if (!lowestLevel) {
    return false;
  }

  // Check if current selection is at the lowest available battery level
  const lowestLevelId = filters.batteryFilters[`${lowestLevel}Id`];
  return Boolean(lowestLevelId);
}
